import type { WorkbookAccessMode } from "@/business/business";
import { WORKBOOK_ACCESS_MODES } from "@/business/constants";
import { isPayrollFeatureEnabled } from "@/business/product-access";
import { useGetCurrentUserWorkbookAccess } from "@/business/query";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { useBusinessProducts } from "@/core/business/use-business-products";
import type { UserRole } from "@/user/user";
import { isAdmin, isTech } from "@/user/utils";

export const canViewPayPlan = ({
  role,
  workbookAccessMode,
}: {
  role: UserRole | undefined;
  workbookAccessMode: WorkbookAccessMode | undefined;
}) => {
  if (!role || isTech(role)) return false;
  if (isAdmin(role)) return true;
  return workbookAccessMode === WORKBOOK_ACCESS_MODES.full;
};

export const usePayPlanAccess = () => {
  const { user } = useAuth();
  const { businessId } = useBusiness();
  const { settings, isLoading: productsLoading } = useBusinessProducts();
  const featureAvailable = isPayrollFeatureEnabled(settings, "pay_plans");
  const needsWorkbookAccess =
    featureAvailable &&
    !!user?.role &&
    !isAdmin(user.role) &&
    !isTech(user.role);
  const workbookAccessQuery = useGetCurrentUserWorkbookAccess(businessId, {
    enabled: needsWorkbookAccess,
  });

  return {
    canViewPayPlan:
      featureAvailable &&
      !workbookAccessQuery.isError &&
      canViewPayPlan({
        role: user?.role,
        workbookAccessMode: workbookAccessQuery.data?.mode,
      }),
    isLoading:
      productsLoading || (needsWorkbookAccess && workbookAccessQuery.isLoading),
  };
};
